import { LineType } from 'features/seismic-3d/models/enums/LineType';
import { IGeopostSeismicLayerParams } from '../../interfaces/IGeopostSeismicLayerParams';
import { TileSourceStrategy } from './TileSourceStrategy';

export class LineTileSourceStrategy extends TileSourceStrategy {
    private lineType: LineType;

    constructor(lineType: LineType, apiUrl: string, onTileRequestError: (xhr: XMLHttpRequest, lineNumber: number, tileWidth: number, startTrace: number, endTrace: number) => void = () => {},
        onSettleTileRequest: (xhr: XMLHttpRequest, lineNumber: number, tileWidth: number, requestSendAt: Date) => void = () => {}) {
        super(apiUrl, onTileRequestError, onSettleTileRequest);
        this.lineType = lineType;
    }

    public getTileUrl = (params: IGeopostSeismicLayerParams) => {
        const [surveyLineStart, surveyLineEnd, surveyLineIncrement] = this.getSurveyLineDimensions(params);
        return super.getTileUrl(params) +
        this.addParamValueToUrl('lineType', this.lineType.toString()) +
        this.addParamValueToUrl('lineNumber', params.lineNumber!.toString()) +
        this.addParamValueToUrl('lineIncrement', surveyLineIncrement.toString()) +
        this.addParamValueToUrl('useTraceRange', params.useTraceRange!.toString()) +
        this.addParamToUrl('minSampleValue') +
        this.addParamToUrl('maxSampleValue') +
        this.addParamToUrl('surveyLineStart') +
        this.addParamToUrl('surveyLineEnd') +
        this.addParamToUrl('lineStart') +
        this.addParamToUrl('lineEnd') +
        this.addParamToUrl('lineStartSample') +
        this.addParamToUrl('lineEndSample');
    };

    public getFullUrl = (src: string, xStart: number, xEnd: number, yStart: number, yEnd: number, minGain: number, maxGain: number, surveyLineStart: number, surveyLineEnd: number) => {
        let fullUrl = src.replace('[lineStart]', Math.floor(xStart).toString())
            .replace('[lineEnd]', Math.floor(xEnd).toString())
            .replace('[lineStartSample]', (yStart).toString())
            .replace('[lineEndSample]', (yEnd).toString())
            .replace('[minSampleValue]', minGain.toString())
            .replace('[maxSampleValue]', maxGain.toString())
            .replace('[surveyLineStart]', Math.round(surveyLineStart).toString())
            .replace('[surveyLineEnd]', Math.round(surveyLineEnd).toString());
        return fullUrl;
    };

    public getEndpoint = () => `${this.apiUrl}/seismic/render/tile`;

    public getSurveyLineDimensions: (params: IGeopostSeismicLayerParams) => [number, number, number, number, number] = (params) => {
        if (this.lineType === LineType.Inline || this.lineType === LineType.Xline) {
            let [ surveyLineStart, baseLineStart ] = this.lineType === LineType.Xline ? [params.surveyInlineStart!, params.baseInlineStart!] : [params.surveyXlineStart!, params.baseXlineStart!];
            let [ surveyLineEnd, baseLineEnd ] = this.lineType === LineType.Xline ? [params.surveyInlineEnd!, params.baseInlineEnd!] : [params.surveyXlineEnd!, params.baseXlineEnd!];
            let surveyLineIncrement = this.lineType === LineType.Xline ? params.surveyInlineIncrement! : params.surveyXlineIncrement!;

            if (surveyLineStart === 0) {
                surveyLineStart = 1;
            }
            if (surveyLineIncrement === 0) {
                surveyLineIncrement = 1;
            }
            if (surveyLineEnd === 0) {
                surveyLineEnd = params.numberOfTraces ?? 0;
            }

            return [surveyLineStart, surveyLineEnd, surveyLineIncrement, baseLineStart, baseLineEnd];
        } else {
            const surveyLineStart = 1;
            const surveyLineEnd = params.numberOfTraces ?? 0;
            const surveyLineIncrement = 1;
            return [surveyLineStart, surveyLineEnd, surveyLineIncrement, surveyLineStart, surveyLineEnd];
        }
    };

    public getTileLoadingKey = (fullUrl: string) => {
        const startTrace = this.extractUrlValue(fullUrl, 'lineStart');
        const endTrace = this.extractUrlValue(fullUrl, 'lineEnd');
        const lineStartSample = this.extractUrlValue(fullUrl, 'lineStartSample');
        const lineEndSample = this.extractUrlValue(fullUrl, 'lineEndSample');

        return `${startTrace}-${endTrace}-${lineStartSample}-${lineEndSample}`;
    };
};